import { useContext, useEffect, useState } from 'react';
import {
    Dropdown,
    IDropdownListItem,
} from '@acko-tech/building-blocks.ui.dropdown';
import { useRouter } from 'next/router';
import { NewPlanStages } from '../constants';
import { MPosContext } from 'utils/context';
import {
    SegmentEventActionTypes,
    SegmentEventActionNames,
    SegmentEventFieldTypes,
    SegmentEventNameTypes,
    SegmentEventPageTypes,
    SegmentEventPages,
} from 'utils/segment/constants';
import { useViewport } from 'utils/responsive';
import { IOption } from './types';
import SnackBar from 'library/snackbar';
import { getLabels } from './utils';
import { PageLoader } from 'utils/styles';
import { getImageBasePath } from 'utils/generics';
import * as Styles from './styles';
import IMEIModelScreen from '../imeiModelScreen';
import HorizontalDivider from 'widgets/HorizontalDivider';
import sendSegmentEvent from 'utils/segment';
import { Permissions } from 'utils/constants';

function MobileModel() {
    const router = useRouter();

    const { isMobile } = useViewport();

    const {
        partnerId,
        planDetails,
        permissions,
        setActiveStage,
        setPlanDetails,
        userDetails,
        permissionsMap,
    } = useContext(MPosContext);

    const [options, setOptions] = useState<IOption[]>([]);
    const [modelOptions, setModelOptions] = useState<IDropdownListItem[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedModelOption, setSelectedModelOption] = useState({
        value: planDetails?.device?.id || '',
        displayValue: planDetails?.device?.name || '',
    });
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [isLoading, setIsLoading] = useState(true);

    /**
     * view segment hook
     */
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sendSegmentEvent(
                SegmentEventNameTypes.VIEW,
                {
                    page_type: SegmentEventPageTypes.MAIN_PAGE,
                    view_name: SegmentEventPages.VIEW_DEVICE_MODEL,
                },
                { userDetails, isMobile }
            );
        }
    }, [window]);

    useEffect(() => {
        setSelectedModelOption({
            value: planDetails?.device?.id || '',
            displayValue: planDetails?.device?.name || '',
        });
    }, [planDetails]);

    useEffect(() => {
        if (!isMobile) return;
        const timeout = setTimeout(() => {
            try {
                const bottomSheetElement =
                    document.querySelector('#bottomSheet');
                if (bottomSheetElement) {
                    const documents =
                        bottomSheetElement.getElementsByTagName('div');

                    if (documents[0]) {
                        documents[0].style.top = '10vh';
                        documents[0].style.maxHeight = '100%';
                        documents[0].style.padding = '20px';
                        documents[0].style.paddingBottom = '0px';

                        const innerDocuments =
                            documents[0].getElementsByTagName('div');
                        if (innerDocuments.length > 3 && innerDocuments[3]) {
                            innerDocuments[3].style.maxHeight = '100%';
                        }
                    }
                }
            } catch {}
        }, 100);

        return () => clearTimeout(timeout);
    }, [showDropdown]);

    useEffect(() => {
        try {
            import('../apis').then(async (req) => {
                const { models, statusCode } = await req.getModelsForPartnerId(
                    permissions.partner_id,
                    permissions.group
                );
                setIsLoading(false);
                if (statusCode === 401) {
                    router.push('/login');
                    setSnackbar({
                        type: 'failure',
                        message: 'Token expired. Redirecting to login page.',
                    });
                    return;
                }
                if (models.length > 0) {
                    setOptions(models);
                    const options = models.map(({ id, name }) => {
                        return {
                            displayValue: name,
                            value: id,
                        };
                    });
                    setModelOptions(options);
                }
            });
        } catch {}
    }, []);

    const toggleDropdown = (show: boolean) => {
        setShowDropdown(show);
        if (!show) {
            const opts = options.map(({ id, name }) => {
                return {
                    displayValue: name,
                    value: id,
                };
            });
            setModelOptions(opts);
        }
    };

    const onSelectModelDropdown = ({
        value,
        displayValue,
    }: IDropdownListItem) => {
        try {
            setSelectedModelOption({
                value,
                displayValue,
            });
            const selectedModel = options.filter(({ id }) => value === id);
            if (!(selectedModel?.length > 0)) {
                return;
            }
            setPlanDetails({
                ...planDetails,
                device: {
                    ...planDetails.device,
                    name: displayValue,
                    id: value,
                    type: selectedModel[0].type,
                    price: selectedModel[0].price,
                    model_mapping_name: selectedModel[0].model_mapping_name,
                },
            });
            setActiveStage(NewPlanStages.PLAN);
            /**
             * segment track event
             */
            sendSegmentEvent(
                SegmentEventNameTypes.ACTION,
                {
                    action_type: SegmentEventActionTypes.TAP_BTN,
                    action_name: SegmentEventActionNames.SELECT_MODEL,
                    button_name: `select_${displayValue}_model`,
                    field_selected: SegmentEventFieldTypes.DROPDOWN,
                },
                { userDetails, isMobile }
            );
        } catch {}
    };

    const { label1, label2 } = getLabels(partnerId);

    return (
        <Styles.Wrapper>
            <>
                {isLoading ? (
                    <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />
                ) : (
                    <Styles.MobileModelContainer>
                        <>
                            <Styles.IMEIValidationContainer>
                                <IMEIModelScreen />
                            </Styles.IMEIValidationContainer>
                        </>
                        {permissionsMap[Permissions.MANUAL_PURCHASE_FLOW] && (
                            <>
                                <HorizontalDivider text="OR" />
                                <Styles.SelectModelText>
                                    {label1}
                                </Styles.SelectModelText>

                                <Styles.SelectModelDropdown>
                                    <Dropdown
                                        className={'mobileModalDropdown'}
                                        show={showDropdown}
                                        value={selectedModelOption}
                                        label={label2}
                                        options={modelOptions}
                                        onChange={onSelectModelDropdown}
                                        toggleDropdown={toggleDropdown}
                                        hideClose
                                        hideHeader
                                        showSearchBar
                                    />
                                </Styles.SelectModelDropdown>
                            </>
                        )}
                    </Styles.MobileModelContainer>
                )}
                {/* <PopularModels onClick={onSelectModel} /> */}
                <SnackBar
                    {...snackbar}
                    handleSnakbarComplete={() =>
                        setSnackbar({ type: '', message: '' })
                    }
                />
            </>
        </Styles.Wrapper>
    );
}

export default MobileModel;
